import React, { createContext, useState, useContext, useCallback } from 'react';
import { ProcessStepModal } from '../components/Modals/ProcessStepModal';

// Create a context for modal handling
const ModalContext = createContext();

// Provider component that will wrap your application
export const ModalProvider = ({ children }) => {
  const [modalConfig, setModalConfig] = useState(null);
  const [modalPromiseResolve, setModalPromiseResolve] = useState(null);

  const showModal = useCallback((config) => {
    return new Promise(resolve => {
      setModalConfig(config);
      setModalPromiseResolve(() => resolve);
    });
  }, []);

  const handleRetry = useCallback(() => {
    if (modalPromiseResolve) {
      modalPromiseResolve({ action: "retry" });
    }
    setModalConfig(null);
  }, [modalPromiseResolve]);

  const handleContinue = useCallback(() => {
    if (modalPromiseResolve) {
      // Filter out error and warning items from current items
      const goodItems = modalConfig.currentItems?.filter(item => 
        !modalConfig.errorItems.some(errorItem => errorItem.sku === item.sku) && 
        !modalConfig.warningItems.some(warningItem => warningItem.sku === item.sku)
      );
      modalPromiseResolve({ action: "remove", items: goodItems });
    }
    setModalConfig(null);
  }, [modalPromiseResolve, modalConfig]);

  const handleCancel = useCallback(() => {
    if (modalPromiseResolve) {
      modalPromiseResolve({ action: "cancel" });
    }
    setModalConfig(null);
  }, [modalPromiseResolve]);

  return (
    <ModalContext.Provider value={{ showModal }}>
      {children}
      {modalConfig && (
        <ProcessStepModal
          isOpen={!!modalConfig}
          title={modalConfig.title}
          message={modalConfig.message}
          currentItems={modalConfig.currentItems || []}
          errorItems={modalConfig.errorItems || []}
          warningItems={modalConfig.warningItems || []}
          step={modalConfig.step}
          canRetry={modalConfig.canRetry}
          canRemove={modalConfig.canRemove}
          onRetry={handleRetry}
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      )}
    </ModalContext.Provider>
  );
};

// Hook to use the modal functionality
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};